import { useCallback, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { useFetchCallback } from '@services/services';
import { Order } from '@interfaces/OrderTypes';
import { MSG_LIST_ERROR } from '@pages/Orders/messages';
import { useGetCommerce } from '@pages/Commerce/hooks/commerce';
import { useOrderActions, useOrdersFromStore } from '@store/actions/orders';
import { useWebSocket } from './useWebSocket';
import { selectOrdersToReconcile } from './reconcileActiveOrders';

// Reconciliación del Kanban "Activos" — red de seguridad para el push WS perdido (CS-139/CS-140).
//
// El push `newOrder` es fire-and-forget: si el socket está caído/idle/stale cuando el backend activa
// un pedido (p.ej. un programado vía `auto_fire` a una hora arbitraria), el pedido NUNCA llega al
// Kanban hasta recargar. Este hook re-trae los pedidos activos y los MERGEA de forma idempotente:
//
//   1. Al (re)conectar el socket — reacciona a `connectionEpoch` del WebSocketContext, que se
//      incrementa en cada `onopen` (alta inicial Y cada reconexión). Inyecta lo activado mientras
//      el socket estaba caído.
//   2. Al recuperar foco/visibilidad — `window` focus y `visibilitychange → visible`. Cubre el caso
//      real del reporte: laptop dormida / pestaña en background cuando se dispara el auto_fire.
//
// El merge usa `addOrder` (dedup por `id` en el reducer) sobre las filas que `selectOrdersToReconcile`
// marca como activas y ausentes del store → sin duplicados, sin parpadeo, sin inyectar programados
// que aún no se activan. Se monta una sola vez (en PrivateGlobalHooks), junto a useIncomingOrder.
export const useReconcileActiveOrders = () => {
  const { connectionEpoch } = useWebSocket();
  const { commerce } = useGetCommerce();
  const orders = useOrdersFromStore();
  const { addOrder } = useOrderActions();
  const { call: fetchOrders } = useFetchCallback<null, { count: number; rows: Order[] }>('get', '/v1/pedido');

  // Refs para que los listeners (registrados una vez) lean SIEMPRE el valor más reciente sin
  // re-suscribirse en cada render (mismo patrón que useIncomingOrder con accountCommerceTokensRef).
  const ordersRef = useRef(orders);
  ordersRef.current = orders;
  const commerceIdRef = useRef(commerce?.id);
  commerceIdRef.current = commerce?.id;
  // Evita reconciliaciones solapadas (varios triggers casi simultáneos: reconexión + focus).
  const inFlightRef = useRef(false);

  const reconcile = useCallback(async () => {
    if (!commerceIdRef.current) return; // sin comercio activo no hay Kanban que reconciliar
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const response = await fetchOrders({ path: '?orders_in_process=true' });
      if (!response?.rows) return;
      const toAdd = selectOrdersToReconcile(response.rows, ordersRef.current);
      // addOrder vuelve a deduplicar por id en el reducer: doble red contra duplicados/parpadeo.
      toAdd.forEach((order) => addOrder(order));
    } catch {
      // Silencioso salvo el error de carga estándar: la reconciliación es best-effort y NO debe
      // romper el realtime ni spamear toasts en cada focus. Reusa el mensaje del listado.
      toast.error(MSG_LIST_ERROR);
    } finally {
      inFlightRef.current = false;
    }
    // fetchOrders/addOrder son estables (useCallback con deps vacías); sin deps reactivas para no
    // recrear la función y re-registrar los listeners.
  }, [fetchOrders, addOrder]);

  const reconcileRef = useRef(reconcile);
  reconcileRef.current = reconcile;

  // Trigger 1: (re)conexión del socket. El epoch arranca en 0; el primer `onopen` lo lleva a 1.
  // Ignoramos el 0 inicial (no hay conexión todavía) para no duplicar el fetch inicial que ya hace
  // useGetOrdersWithStatus al montar el Kanban.
  useEffect(() => {
    if (connectionEpoch === 0) return;
    reconcileRef.current();
  }, [connectionEpoch]);

  // Trigger 2: foco / visibilidad. Cubre laptop dormida / pestaña en background al dispararse el
  // auto_fire. Registrado una sola vez; lee el valor fresco vía reconcileRef.
  useEffect(() => {
    const onFocus = () => reconcileRef.current();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') reconcileRef.current();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  return { reconcile };
};

export default useReconcileActiveOrders;
